import type { AdminUser } from '../types';
import { IconDots } from './icons';
import { logout } from '../lib/auth';
import { NavPip, type PipColor } from './NavPip';
import { VIEW_ICON } from './nav-icons';
import {
  type View, type SectionId, VIEW_LABEL, SECTION_LABEL, SECTION_VIEWS,
} from '../lib/views';

interface Props {
  view: View;
  setView: (v: View) => void;
  user: AdminUser | null;
  /**
   * Map partielle des slots par view. count est affiché à droite du label,
   * pip est un dot 6px coloré (cf. NavPip). Tout est optionnel.
   * Phase 1 : seul fundsCount est hydraté côté App.tsx. Le reste viendra
   * route par route dans les PR suivantes.
   */
  slotsByView?: Partial<Record<View, { count?: number; pip?: { color: PipColor; label?: string } }>>;
}

/**
 * Sidebar admin, refondue 2026-05-28.
 *
 * Trois sections : Founders, Content, Commun (cf. BRIEF_admin-refonte_v2).
 * Style hérité de l'ancien Sidebar (244px, bg-white, active = founders-soft).
 *
 * Règle "un pip d'urgence par section maximum" (cf. P4 brief section 13) :
 * appliquée par la fonction enforceOnePipPerSection avant le rendu.
 */

const SECTIONS_ORDER: SectionId[] = ['founders', 'content', 'commun'];

function enforceOnePipPerSection<T extends { pip?: { color: PipColor; label?: string } }>(
  items: T[],
): T[] {
  let urgentSeen = false;
  return items.map((item) => {
    if (item.pip && (item.pip.color === 'red' || item.pip.color === 'gold')) {
      if (urgentSeen) return { ...item, pip: undefined };
      urgentSeen = true;
    }
    return item;
  });
}

export function Sidebar({ view, setView, user, slotsByView }: Props) {
  const initials = (user?.email ?? 'GB').slice(0, 2).toUpperCase();

  return (
    <aside className="hidden md:flex w-[244px] shrink-0 bg-white border-r border-line-soft sticky top-0 h-screen flex-col">
      {/* Brand */}
      <div className="px-5 pt-5 pb-4 flex items-center gap-2.5">
        <div className="w-8 h-8 rounded-md bg-navy text-white grid place-items-center font-display text-[15px] tracking-tightish">F</div>
        <div>
          <div className="h-display text-[15px] leading-tight">Finance for Quantum</div>
          <div className="text-[11px] text-ink-muted leading-tight">Admin console</div>
        </div>
      </div>

      {/* Nav, 3 sections */}
      <nav className="px-3 text-[13.5px] flex-1 overflow-y-auto pb-3">
        {SECTIONS_ORDER.map((sectionId, sectionIndex) => {
          const views = SECTION_VIEWS[sectionId];
          const hydratedItems = enforceOnePipPerSection(
            views.map((v) => ({
              view: v,
              label: VIEW_LABEL[v],
              icon: VIEW_ICON[v],
              count: slotsByView?.[v]?.count,
              pip: slotsByView?.[v]?.pip,
            })),
          );

          return (
            <div key={sectionId} className={sectionIndex === 0 ? '' : 'pt-4'}>
              <div className="px-2.5 pb-1.5 text-[11px] uppercase tracking-wider text-ink-faint font-medium">
                {SECTION_LABEL[sectionId]}
              </div>
              <div className="space-y-0.5">
                {hydratedItems.map((item) => {
                  const Icon = item.icon;
                  const active = view === item.view;
                  return (
                    <button
                      key={item.view}
                      onClick={() => setView(item.view)}
                      className={
                        'w-full flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-md text-left transition ' +
                        (active ? 'bg-founders-soft text-founders-deep' : 'text-ink hover:bg-cream-tint')
                      }
                      aria-current={active ? 'page' : undefined}
                    >
                      <span className="flex items-center gap-2.5 min-w-0">
                        <span className={active ? 'text-founders-deep shrink-0' : 'text-ink-muted shrink-0'}>
                          <Icon />
                        </span>
                        <span className="truncate">{item.label}</span>
                      </span>
                      <span className="flex items-center gap-1.5 shrink-0">
                        {item.pip && <NavPip color={item.pip.color} ariaLabel={item.pip.label} />}
                        {typeof item.count === 'number' && (
                          <span className="num text-[11px] text-ink-muted">{item.count.toLocaleString('fr-FR')}</span>
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </nav>

      {/* User footer */}
      <div className="p-3 border-t border-line-soft">
        <button onClick={() => logout()} className="w-full flex items-center gap-2.5 px-2 py-2 rounded-md hover:bg-cream-tint">
          <span className="w-7 h-7 rounded-full bg-navy text-white grid place-items-center text-[11px] font-medium">{initials}</span>
          <span className="min-w-0 flex-1 text-left">
            <span className="block text-[13px] font-medium leading-tight truncate">{user?.full_name ?? 'Admin'}</span>
            <span className="block text-[11px] text-ink-muted leading-tight truncate">{user?.email ?? ''}</span>
          </span>
          <IconDots className="w-4 h-4 text-ink-faint" />
        </button>
      </div>
    </aside>
  );
}
